import { CircularProgress } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Checkbox from "@saleor/components/Checkbox";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import Hr from "@saleor/components/Hr";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import useSearchQuery from "@saleor/hooks/useSearchQuery";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchCustomers_search_edges_node } from "@saleor/searches/types/SearchCustomers";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";
interface FormData {
  allCustomers: boolean;
  customers: string[];
}

export interface AssignCustomerSelectDialogProps extends FetchMoreProps {
  confirmButtonState: ConfirmButtonTransitionState;
  customers: SearchCustomers_search_edges_node[];
  initial: any;
  open: boolean;
  onClose: () => void;
  onFetch: (value: string) => void;
  onConfirm: (data: FormData) => void;
}

const useStyles = makeStyles(
  theme => ({
    checkboxCell: {
      paddingLeft: 0
    },
    container: {
      maxHeight: 500
    },
    dialog: {
      maxHeight: "100%"
    },
    heading: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(2)
    },
    wideCell: {
      width: "100%"
    }
  }),
  { name: "AssignCustomerSelectDialog" }
);
const AssignCustomerSelectDialog: React.FC<AssignCustomerSelectDialogProps> = props => {
  const {
    confirmButtonState,
    onClose,
    customers,
    onFetchMore,
    open,
    hasMore,
    initial,
    onFetch,
    onConfirm
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});
  // const container = React.useRef<HTMLDivElement>();
  // const containerHeight = container.current?.scrollHeight - 130;

  const intl = useIntl();

  const initialValue = maybe(() => initial.map(({ node }) => node.id));
  const initialForm: FormData = {
    allCustomers: false,
    customers: initialValue
  };

  const [query, onQueryChange] = useSearchQuery(onFetch);
  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth="sm">
      <Form initial={initialForm} onSubmit={onConfirm}>
        {({ data, change }) => {
          const customerSelection = customers.reduce((acc, customer) => {
            acc[customer.id] = !!data.customers.find(
              selectedcustomers => selectedcustomers === customer?.id
            );
            return acc;
          }, {});

          return (
            <div>
              <DialogTitle>
                <FormattedMessage
                  defaultMessage="Assign customers"
                  description="dialog header"
                />
              </DialogTitle>
              <DialogContent>
                <TextField
                  id="filterByCustomerTxt"
                  name="query"
                  value={query}
                  onChange={onQueryChange}
                  label={intl.formatMessage({
                    defaultMessage: "Filter by Customer",
                    description: "search box label"
                  })}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Search by name, email",
                    description: "search box placeholder"
                  })}
                  fullWidth
                />
              </DialogContent>
              <Hr />
              <DialogContent className={classes.container}>
                <Typography className={classes.heading} variant="subtitle1">
                  <FormattedMessage
                    defaultMessage="Active Customer"
                    description="customer selection"
                  />
                </Typography>
                <InfiniteScroll
                  pageStart={0}
                  loadMore={onFetchMore}
                  hasMore={hasMore}
                  useWindow={false}
                  loader={
                    <div
                      className={
                        scrollableDialogClasses.loadMoreLoaderContainer
                      }
                      key={0}
                    >
                      <CircularProgress size={16} />
                    </div>
                  }
                  threshold={10}
                >
                  <ResponsiveTable>
                    <TableBody>
                      {customers &&
                        customers.map((customer, index) => {
                          const isChecked = customerSelection[customer.id];

                          return (
                            <TableRow key={customer.id}>
                              <TableCell
                                className={classes.wideCell}
                                id={`assignCustomerNameRow${index}`}
                              >
                                {customer.firstName}
                                {customer.lastName}
                              </TableCell>
                              <TableCell
                                padding="checkbox"
                                className={classes.checkboxCell}
                              >
                                <Checkbox
                                  id={`assignCustomerCheckboxRow${index}`}
                                  checked={isChecked}
                                  onChange={() =>
                                    isChecked
                                      ? change({
                                          target: {
                                            name: "customers" as keyof FormData,
                                            value: data.customers.filter(
                                              selectedcustomers =>
                                                selectedcustomers !==
                                                customer.id
                                            )
                                          }
                                        } as any)
                                      : change({
                                          target: {
                                            name: "customers" as keyof FormData,
                                            value: [
                                              ...data.customers,
                                              customer.id
                                            ]
                                          }
                                        } as any)
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </ResponsiveTable>
                </InfiniteScroll>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} id="assignCustomersBackBtn">
                  <FormattedMessage {...buttonMessages.back} />
                </Button>
                <ConfirmButton
                  idConfirmButton="assignCustomersBtn"
                  transitionState={confirmButtonState}
                  color="primary"
                  variant="contained"
                  type="submit"
                >
                  <FormattedMessage
                    defaultMessage="Assign customers"
                    description="button"
                  />
                </ConfirmButton>
              </DialogActions>
            </div>
          );
        }}
      </Form>
    </Dialog>
  );
};
AssignCustomerSelectDialog.displayName = "AssignCustomerSelectDialog";
export default AssignCustomerSelectDialog;
